import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '@medikei/ui';
import {
  BarChart,
  Users,
  CreditCard,
  Calendar,
  TrendingUp,
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';
import { httpClient } from '@/api';
import { useToast } from '@/components/ui/use-toast';

interface AdminDashboardStats {
  totalDoctors: number;
  pendingDoctors: number;
  approvedDoctors: number;
  blockedDoctors: number;
  totalPatients: number;
  totalAppointments: number;
  completedAppointments: number;
  pendingPayouts: number;
  totalRevenue: number;
  appointmentsToday: number;
  newPatientsThisMonth: number;
}

interface DashboardStats {
  totalUsers: number;
  totalDoctors: number;
  totalPatients: number;
  totalAppointments: number;
  totalRevenue: number;
  pendingPayouts: number;
  activeSessions: number;
}

interface RecentActivity {
  id: string;
  type: 'USER_REGISTERED' | 'APPOINTMENT_COMPLETED' | 'PAYMENT_RECEIVED' | 'WITHDRAWAL_REQUEST';
  description: string;
  timestamp: string;
}

interface PendingApproval {
  id: string;
  type: 'DOCTOR' | 'PATIENT';
  name: string;
  email: string;
  submittedAt: string;
}

interface DoctorsApiResponse {
  data: Array<{
    id: string;
    displayName: string | null;
    email: string;
    createdAt: string;
  }>;
}

function mapAuditActionToActivityType(action: string): RecentActivity['type'] {
  if (action.includes('USER') || action.includes('REGISTER')) return 'USER_REGISTERED';
  if (action.includes('APPOINTMENT') || action.includes('COMPLETE')) return 'APPOINTMENT_COMPLETED';
  if (action.includes('PAYMENT') || action.includes('RECEIVE')) return 'PAYMENT_RECEIVED';
  return 'WITHDRAWAL_REQUEST';
}

function formatAuditActionDescription(
  action: string,
  entity?: string,
  entityId?: string,
  targetName?: string,
): string {
  const subject = targetName ? `: ${targetName}` : entityId ? ` (#${entityId.slice(0, 8)})` : '';
  switch (action) {
    case 'DOCTOR_APPROVED':
      return `Médico aprovado e conta ativada${subject}`;
    case 'DOCTOR_REJECTED':
      return `Cadastro de médico rejeitado${subject}`;
    case 'DOCTOR_BLOCKED':
      return `Médico bloqueado administrativamente${subject}`;
    case 'DOCTOR_UNBLOCKED':
      return `Médico desbloqueado${subject}`;
    case 'DOCTOR_DOCUMENT_APPROVED':
      return `Documento médico aprovado na auditoria${targetName ? ` (${targetName})` : ''}`;
    case 'DOCTOR_DOCUMENT_REJECTED':
      return `Documento médico recusado na auditoria${targetName ? ` (${targetName})` : ''}`;
    case 'USER_REGISTERED':
    case 'USER_CREATED':
      return `Novo usuário cadastrado na plataforma${subject}`;
    case 'USER_BLOCKED':
      return `Usuário bloqueado pelo administrador${subject}`;
    case 'APPOINTMENT_COMPLETED':
    case 'APPOINTMENT_FINISHED':
      return `Consulta médica finalizada`;
    case 'PAYMENT_RECEIVED':
      return `Pagamento de consulta confirmado`;
    case 'WITHDRAWAL_REQUEST':
      return `Solicitação de saque recebida${subject}`;
    case 'WITHDRAWAL_APPROVED':
      return `Saque autorizado para pagamento${subject}`;
    default:
      return `${action.replace(/_/g, ' ').toLowerCase()} em ${entity || 'registro'}${subject}`;
  }
}

export function AdminDashboardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: adminStats, isLoading: statsLoading } = useQuery<AdminDashboardStats>({
    queryKey: ['admin', 'stats'],
    queryFn: () => httpClient.get<AdminDashboardStats>('/admin/dashboard'),
  });

  const approveMutation = useMutation({
    mutationFn: async (doctorId: string) => {
      await httpClient.patch(`/admin/doctors/${doctorId}/approve`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'approvals'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'doctors'] });
      toast({
        title: 'Médico aprovado!',
        description: 'O cadastro médico foi validado e ativado com sucesso.',
      });
    },
    onError: (err: any) => {
      toast({
        title: 'Erro ao aprovar',
        description: err?.response?.data?.message || err?.message || 'Tente novamente.',
        variant: 'error',
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (doctorId: string) => {
      await httpClient.patch(`/admin/doctors/${doctorId}/reject`, { reason: 'Documentação pendente ou divergente' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'approvals'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'doctors'] });
      toast({
        title: 'Cadastro rejeitado',
        description: 'O médico foi notificado para regularização dos dados.',
      });
    },
    onError: (err: any) => {
      toast({
        title: 'Erro ao rejeitar',
        description: err?.response?.data?.message || err?.message || 'Tente novamente.',
        variant: 'error',
      });
    },
  });

  const stats: DashboardStats = {
    totalUsers: (adminStats?.totalDoctors || 0) + (adminStats?.totalPatients || 0),
    totalDoctors: adminStats?.totalDoctors || 0,
    totalPatients: adminStats?.totalPatients || 0,
    totalAppointments: adminStats?.totalAppointments || 0,
    totalRevenue: adminStats?.totalRevenue || 0,
    pendingPayouts: adminStats?.pendingPayouts || 0,
    activeSessions: 0,
  };

  const { data: recentActivity } = useQuery<RecentActivity[]>({
    queryKey: ['admin', 'activity'],
    queryFn: async () => {
      const json = await httpClient.get<{ data: any[] }>('/admin/audit-logs?limit=10');
      return (json.data || []).map((log: any) => ({
        id: log.id,
        type: mapAuditActionToActivityType(log.action),
        description: formatAuditActionDescription(
          log.action,
          log.entity,
          log.entityId,
          log.targetName || log.newData?.doctorName || log.newData?.name,
        ),
        timestamp: log.createdAt,
      }));
    },
  });

  const { data: pendingApprovals } = useQuery<PendingApproval[]>({
    queryKey: ['admin', 'approvals'],
    queryFn: async () => {
      const params = new URLSearchParams({ status: 'PENDING', limit: '10' });
      const json = await httpClient.get<DoctorsApiResponse>(`/admin/doctors?${params}`);
      return json.data.map((doc) => ({
        id: doc.id,
        type: 'DOCTOR' as const,
        name: doc.displayName || 'N/A',
        email: doc.email || 'N/A',
        submittedAt: doc.createdAt,
      }));
    },
  });

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (dateStr: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateStr));
  };

  return (
    <div className="space-y-6 text-foreground">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Painel Administrativo</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Visão consolidada da operação, métricas e auditoria da plataforma</p>
        </div>
        <span className="text-xs text-muted-foreground bg-muted/50 px-3 py-1.5 rounded-full border border-border/60 self-start sm:self-auto">
          Última atualização: {new Date().toLocaleString('pt-BR')}
        </span>
      </div>

      {/* Cards de Métricas Principais - Interativos com Acesso Rápido */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link
          to="/admin/users"
          className="p-4 rounded-2xl border border-border bg-card hover:border-primary/40 hover:shadow-md transition-all group flex items-center justify-between"
        >
          <div className="flex items-center gap-3.5">
            <div className="p-3 bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-xl group-hover:scale-105 transition-transform">
              <Users className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-medium">Total de Usuários</p>
              <p className="text-2xl font-bold text-foreground mt-0.5">{stats?.totalUsers.toLocaleString('pt-BR')}</p>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-muted-foreground/50 group-hover:text-primary group-hover:translate-x-1 transition-all" />
        </Link>

        <Link
          to="/admin/financeiro"
          className="p-4 rounded-2xl border border-border bg-card hover:border-primary/40 hover:shadow-md transition-all group flex items-center justify-between"
        >
          <div className="flex items-center gap-3.5">
            <div className="p-3 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-xl group-hover:scale-105 transition-transform">
              <TrendingUp className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-medium">Receita Total</p>
              <p className="text-2xl font-bold text-foreground mt-0.5">{formatCurrency(stats?.totalRevenue || 0)}</p>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-muted-foreground/50 group-hover:text-primary group-hover:translate-x-1 transition-all" />
        </Link>

        <Link
          to="/admin/consultas"
          className="p-4 rounded-2xl border border-border bg-card hover:border-primary/40 hover:shadow-md transition-all group flex items-center justify-between"
        >
          <div className="flex items-center gap-3.5">
            <div className="p-3 bg-purple-500/10 text-purple-600 dark:text-purple-400 rounded-xl group-hover:scale-105 transition-transform">
              <Calendar className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-medium">Consultas Realizadas</p>
              <p className="text-2xl font-bold text-foreground mt-0.5">{stats?.totalAppointments.toLocaleString('pt-BR')}</p>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-muted-foreground/50 group-hover:text-primary group-hover:translate-x-1 transition-all" />
        </Link>

        <Link
          to="/admin/saques"
          className="p-4 rounded-2xl border border-border bg-card hover:border-primary/40 hover:shadow-md transition-all group flex items-center justify-between"
        >
          <div className="flex items-center gap-3.5">
            <div className="p-3 bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-xl group-hover:scale-105 transition-transform">
              <CreditCard className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground font-medium">Saques Pendentes</p>
              <p className="text-2xl font-bold text-foreground mt-0.5">{stats?.pendingPayouts}</p>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-muted-foreground/50 group-hover:text-primary group-hover:translate-x-1 transition-all" />
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Aprovações Pendentes */}
        <Card className="rounded-2xl border border-border bg-card shadow-xs overflow-hidden">
          <CardHeader className="p-5 border-b border-border/60">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-bold flex items-center gap-2 text-foreground">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                <span>Aprovações Médicas Pendentes</span>
              </CardTitle>
              <Link to="/admin/medicos?status=PENDING" className="text-xs font-semibold text-primary hover:underline">
                Ver todos
              </Link>
            </div>
          </CardHeader>
          <CardContent className="p-5">
            {pendingApprovals?.length === 0 ? (
              <div className="text-center py-10 space-y-2">
                <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
                <p className="text-sm font-semibold text-foreground">Nenhuma aprovação pendente</p>
                <p className="text-xs text-muted-foreground">Todos os cadastros médicos estão auditados e em dia.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingApprovals?.map((approval) => (
                  <div key={approval.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 bg-muted/40 rounded-xl border border-border/60">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-foreground text-sm truncate">{approval.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{approval.email}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">Solicitado em {formatDate(approval.submittedAt)}</p>
                    </div>
                    <div className="flex items-center gap-2 self-end sm:self-center shrink-0">
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/medicos?search=${encodeURIComponent(approval.name)}`)}
                        className="px-2.5 py-1.5 text-xs font-semibold rounded-lg border border-border hover:bg-muted text-foreground transition-colors cursor-pointer"
                        title="Ver documentos e cadastro completo"
                      >
                        Ver Documentos
                      </button>
                      <button
                        type="button"
                        onClick={() => approveMutation.mutate(approval.id)}
                        disabled={approveMutation.isPending}
                        className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors shadow-xs cursor-pointer"
                      >
                        Aprovar
                      </button>
                      <button
                        type="button"
                        onClick={() => rejectMutation.mutate(approval.id)}
                        disabled={rejectMutation.isPending}
                        className="px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors shadow-xs cursor-pointer"
                      >
                        Rejeitar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Atividade Recente */}
        <Card className="rounded-2xl border border-border bg-card shadow-xs overflow-hidden">
          <CardHeader className="p-5 border-b border-border/60">
            <CardTitle className="text-base font-bold flex items-center gap-2 text-foreground">
              <BarChart className="h-5 w-5 text-blue-500" />
              <span>Atividade Recente da Plataforma</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5">
            <div className="space-y-3 max-h-[360px] overflow-y-auto">
              {recentActivity?.map((activity) => (
                <div key={activity.id} className="flex items-start gap-3 p-3 bg-muted/40 rounded-xl border border-border/60">
                  <div className={`w-2.5 h-2.5 mt-1.5 rounded-full shrink-0 ${
                    activity.type === 'USER_REGISTERED' ? 'bg-blue-500' :
                    activity.type === 'APPOINTMENT_COMPLETED' ? 'bg-emerald-500' :
                    activity.type === 'PAYMENT_RECEIVED' ? 'bg-purple-500' :
                    'bg-amber-500'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground leading-snug">{activity.description}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{formatDate(activity.timestamp)}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cards de Resumo Operacional */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link
          to="/admin/medicos"
          className="p-5 rounded-2xl border border-border bg-card hover:border-primary/40 hover:shadow-md transition-all text-center block group cursor-pointer"
        >
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Médicos Ativos</p>
          <p className="text-3xl font-extrabold text-blue-600 dark:text-blue-400 mt-1">{stats?.totalDoctors}</p>
          <p className="text-xs text-primary font-medium mt-1 group-hover:underline">Gerenciar médicos &rarr;</p>
        </Link>
        <Link
          to="/admin/pacientes"
          className="p-5 rounded-2xl border border-border bg-card hover:border-primary/40 hover:shadow-md transition-all text-center block group cursor-pointer"
        >
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Pacientes Cadastrados</p>
          <p className="text-3xl font-extrabold text-emerald-600 dark:text-emerald-400 mt-1">{stats?.totalPatients}</p>
          <p className="text-xs text-primary font-medium mt-1 group-hover:underline">Ver pacientes &rarr;</p>
        </Link>
        <Link
          to="/admin/filas"
          className="p-5 rounded-2xl border border-border bg-card hover:border-primary/40 hover:shadow-md transition-all text-center block group cursor-pointer"
        >
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Sessões & Filas Ativas</p>
          <p className="text-3xl font-extrabold text-purple-600 dark:text-purple-400 mt-1">{stats?.activeSessions}</p>
          <p className="text-xs text-primary font-medium mt-1 group-hover:underline">Monitorar filas &rarr;</p>
        </Link>
      </div>
    </div>
  );
}
